import { useState, useCallback, useRef } from 'react';
import axios from "axios";
import { useAuth0 } from "../react-auth0-spa";
import { useAppContext } from "../context";
import config from "../api_config.json";

// GraphQL query for Annunci Funebri
const annunciFunebriQuery = `
query AnnunciFunebriQuery($first: Int!, $skip: Int, $where: Annunci_funebriWhereInput, $order: [Annunci_funebriOrder!]) {
  annunci_funebris(
    first: $first,
    skip: $skip,
    where: $where,
    order: $order
  ) {
    count
    edges {
      node {
        objectId
        createdAt
        updatedAt
        codice_annuncio
        link_annuncifunebri
        link_cliente
        foto {
          url
        }
        org_pointer {
          objectId
          nome
        }
      }
    }
  }
}`.replace(/\r?\n|\r/g, "").replace(/\t/g, " ");

/**
 * Hook for managing Annunci Funebri data with GraphQL and infinite scroll
 * Works for both single organization and global views
 */
export const useAnnunciFunebriData = (orgId = null) => {
  const [loading, setLoading] = useState(false);
  const [initialFetchDone, setInitialFetchDone] = useState(false);
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);
  const [hasMore, setHasMore] = useState(true);

  const { getTokenSilently } = useAuth0();
  const { isProd } = useAppContext();
  const env = isProd ? "prod" : "dev";
  
  const skipRef = useRef(0);
  const pageSize = 20;

  /**
   * Fetch Annunci Funebri data
   */
  const fetchData = useCallback(async (append = false) => {
    const graphqlUrl = `${config.baseUrl[env]}/graphql?raw=true`;
    
    try {
      if (!append) {
        setLoading(true);
      }

      const token = await getTokenSilently();
      
      // Build where clause - filter by organization if orgId is provided
      const where = orgId ? {
        org_pointer: {
          have: {
            objectId: {
              equalTo: orgId
            }
          }
        }
      } : {};
      
      const variables = {
        first: pageSize,
        skip: append ? skipRef.current : 0,
        where: where,
        order: ["createdAt_DESC"]
      };

      const response = await axios({
        method: "post",
        url: graphqlUrl,
        data: {
          query: annunciFunebriQuery,
          variables: variables
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const responseData = response.data;
      
      if (responseData?.annunci_funebris) {
        const { edges, count: totalCount } = responseData.annunci_funebris;
        const results = edges.map(edge => edge.node);

        if (append) {
          setItems(prev => [...prev, ...results]);
        } else {
          setItems(results);
        }

        setCount(totalCount || 0);

        // Update skip for next pagination
        skipRef.current = append 
          ? skipRef.current + results.length
          : results.length;

        // Check if there's more data
        setHasMore((append ? skipRef.current : results.length) < (totalCount || 0));
      }

      if (!append) {
        setLoading(false);
        setInitialFetchDone(true);
      }
    } catch (error) {
      console.error('Error fetching Annunci Funebri:', error);
      if (!append) {
        setLoading(false);
        setInitialFetchDone(true);
      }
    }
  }, [orgId, getTokenSilently, env, pageSize]);

  /**
   * Initial fetch
   */
  const fetchInitialData = useCallback(async () => {
    // Reset skip counter
    skipRef.current = 0;
    await fetchData(false);
  }, [fetchData]);

  /**
   * Load more data
   */
  const loadMore = useCallback(() => {
    if (!hasMore || loading) return;
    fetchData(true);
  }, [hasMore, loading, fetchData]);

  return {
    loading,
    initialFetchDone,
    items,
    count,
    hasMore,
    fetchInitialData,
    loadMore,
  };
};